import useSesame from '@oberoninternal/travelbase-ds/hooks/useSesame';
import React, { FC, useState } from 'react';
import { MyOrderFragment, OrderItem_Booking_Fragment } from '../../../generated/graphql';
import createUnitTitle from '../../../utils/createUnitTitle';
import { applicable, not } from '../../../utils/fp';
import { isDeclined, isDone, isRecent, isReviewable } from '../../../utils/order';
import { orderItemIsBooking } from '../../OrderBooking';
import ReviewModal from '../../ReviewModal';
import ReviewTile from '../../ReviewTile';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import { FormattedMessage, useIntl } from 'react-intl';
import SectionTitleWrapper from './SectionTitleWrapper';
import EmptyState from './EmptyState';
import Anchor from '../../designsystem/Anchor';
import Link from 'next/link';

interface Props {
    data: MyOrderFragment[];
}

const BookingReviews: FC<React.PropsWithChildren<Props>> = ({ data }) => {
    const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
    const { formatMessage } = useIntl();
    const [selectedBookingWithoutReviewId, setSelectedBookingWithoutReviewId] = useState<string | null>(null);

    const { onClose: closeReviewModal, open: reviewIsOpen, onOpen: openReviewModal } = useSesame();
    const { onClose: closeWithoutReviewModal, open: withoutreviewIsOpen, onOpen: openWithoutReviewModal } = useSesame();

    const orderItemsWithoutReview = data.reduce<OrderItem_Booking_Fragment[]>(
        (curr, next) =>
            curr.concat(
                next.orderItems
                    .filter(orderItemIsBooking)
                    .filter(applicable(orderItemIsBooking, isDone, isReviewable, isRecent, not(isDeclined)))
            ),
        []
    );

    const orderItemsWithReview = data.reduce<OrderItem_Booking_Fragment[]>(
        (curr, next) => curr.concat(next.orderItems.filter(orderItemIsBooking).filter(item => item.review !== null)),
        []
    );

    const selectedBookingWithoutReview =
        selectedBookingWithoutReviewId &&
        data
            .flatMap(order => order.orderItems)
            .find((item): item is OrderItem_Booking_Fragment => item.id === selectedBookingWithoutReviewId);

    const selectedBooking =
        selectedBookingId &&
        data
            .flatMap(order => order.orderItems)
            .find((item): item is OrderItem_Booking_Fragment => item.id === selectedBookingId);

    if (orderItemsWithReview.length === 0 && orderItemsWithoutReview.length === 0) {
        return (
            <EmptyState
                title={<FormattedMessage defaultMessage="Er zijn nog geen boekingen om te beoordelen." />}
                description={
                    <FormattedMessage
                        defaultMessage="Laat na je bezoek aan een <activityLink>activiteit</activityLink> of <accoLink>accommodatie</accoLink> een beoordeling achter."
                        values={{
                            activityLink: children => (
                                <Link href="/agenda" legacyBehavior>
                                    <Anchor>{children}</Anchor>
                                </Link>
                            ),
                            accoLink: children => (
                                <Link href="/search" legacyBehavior>
                                    <Anchor>{children}</Anchor>
                                </Link>
                            ),
                        }}
                    />
                }
            />
        );
    }

    return (
        <>
            {orderItemsWithoutReview.length !== 0 && (
                <SectionTitleWrapper>
                    <Title elementType="h3">
                        <FormattedMessage defaultMessage="Accommodaties die wachten op jouw beoordelingen" />
                    </Title>
                </SectionTitleWrapper>
            )}
            {orderItemsWithoutReview.map((item, i) => (
                <ReviewTile
                    key={i}
                    title={
                        item.rentalUnit
                            ? createUnitTitle(
                                  item.rentalUnit.name,
                                  item.rentalUnit.accommodation.name,
                                  item.rentalUnit.accommodation.hasPublicPage
                              )
                            : formatMessage({
                                  defaultMessage: 'Deze accommodatie is niet meer beschikbaar',
                              })
                    }
                    thumbnail={item.rentalUnit?.jpegThumbnail!}
                    webThumbnail={item.rentalUnit?.webpThumbnail!}
                    arrivalDate={item.arrivalDate}
                    onLeaveReviewClick={() => {
                        setSelectedBookingWithoutReviewId(item.id ?? null);
                        openWithoutReviewModal();
                    }}
                    isUnavailable={!item.rentalUnit}
                />
            ))}
            {orderItemsWithReview.length !== 0 && (
                <SectionTitleWrapper>
                    <Title elementType="h3">
                        <FormattedMessage defaultMessage="Jouw beoordelingen" />
                    </Title>
                </SectionTitleWrapper>
            )}
            {orderItemsWithReview.map((item, i) => (
                <ReviewTile
                    key={i}
                    title={
                        item.rentalUnit
                            ? createUnitTitle(
                                  item.rentalUnit?.name,
                                  item.rentalUnit?.accommodation.name,
                                  item.rentalUnit?.accommodation.hasPublicPage
                              )
                            : formatMessage({ defaultMessage: 'Deze accommodatie is niet meer beschikbaar' })
                    }
                    isUnavailable={!item.rentalUnit}
                    thumbnail={item.rentalUnit?.jpegThumbnail!}
                    webThumbnail={item.rentalUnit?.webpThumbnail!}
                    arrivalDate={item.arrivalDate}
                    onReviewClick={() => {
                        setSelectedBookingId(item.id ?? null);
                        openReviewModal();
                    }}
                    review={item.review!}
                />
            ))}
            {selectedBookingWithoutReview && (
                <ReviewModal
                    data={selectedBookingWithoutReview}
                    open={withoutreviewIsOpen}
                    onClose={closeWithoutReviewModal}
                />
            )}
            {selectedBooking && <ReviewModal data={selectedBooking} open={reviewIsOpen} onClose={closeReviewModal} />}
        </>
    );
};

export default BookingReviews;
